// pages/admin/UnitTypesPage.tsx
/**
 * @description Unit types management page - CRUD for unit types like Bachelor, 1-BR, 2-BR
 */
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Plus,
  Pencil,
  Trash2,
  ChevronLeft,
  Loader2,
  AlertTriangle,
  GripVertical,
  Home
} from 'lucide-react';
import { AdminLayout } from '../../components/layout';
import { Button, Modal, Input } from '../../components/ui';
import { useToast } from '../../components/ui/Toast';
import {
  getUnitTypes,
  createUnitType,
  updateUnitType,
  deleteUnitType,
  type UnitType
} from '../../lib/api';

export function UnitTypesPage() {
  const { showToast } = useToast();

  // Data state
  const [unitTypes, setUnitTypes] = useState<UnitType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [editingType, setEditingType] = useState<UnitType | null>(null);
  const [deletingType, setDeletingType] = useState<UnitType | null>(null);
  const [saving, setSaving] = useState(false);

  // Fetch unit types
  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await getUnitTypes();
      if (fetchError) {
        setError(fetchError);
      } else if (data) {
        setUnitTypes(data.unit_types);
      }
      setLoading(false);
    }

    fetchData();
  }, []);

  const handleAddType = () => {
    setEditingType(null);
    setIsModalOpen(true);
  };

  const handleEditType = (type: UnitType) => {
    setEditingType(type);
    setIsModalOpen(true);
  };

  const handleDeleteClick = (type: UnitType) => {
    setDeletingType(type);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deletingType) return;

    setSaving(true);
    const { error } = await deleteUnitType(deletingType.id);
    setSaving(false);

    if (error) {
      showToast(`Failed to delete: ${error}`, 'error');
    } else {
      setUnitTypes((prev) => prev.filter((t) => t.id !== deletingType.id));
      showToast(`"${deletingType.name}" deleted`, 'success');
      setIsDeleteModalOpen(false);
      setDeletingType(null);
    }
  };

  const handleSaveType = async (formData: { name: string }) => {
    setSaving(true);

    if (editingType) {
      // Update existing
      const { error } = await updateUnitType(editingType.id, formData);
      setSaving(false);

      if (error) {
        showToast(`Failed to update: ${error}`, 'error');
        return;
      }

      setUnitTypes((prev) =>
        prev.map((t) => (t.id === editingType.id ? { ...t, ...formData } : t))
      );
      showToast(`"${formData.name}" updated`, 'success');
    } else {
      // Create new
      const { data, error } = await createUnitType(formData);
      setSaving(false);

      if (error) {
        showToast(`Failed to create: ${error}`, 'error');
        return;
      }

      if (data) {
        setUnitTypes((prev) => [...prev, { id: data.id, name: data.name, sort_order: data.sort_order }]);
        showToast(`"${formData.name}" created`, 'success');
      }
    }

    setIsModalOpen(false);
  };

  // Loading state
  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
          <span className="ml-3 text-gray-600">Loading unit types...</span>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-4 lg:p-8">
        {/* Breadcrumb */}
        <div className="mb-4 lg:mb-6">
          <Link
            to="/admin"
            className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700"
          >
            <ChevronLeft size={16} className="mr-1" />
            Back to Dashboard
          </Link>
        </div>

        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6 lg:mb-8">
          <div>
            <div className="flex items-center gap-2">
              <Home size={24} className="text-primary" />
              <h1 className="text-xl lg:text-2xl font-semibold text-gray-900">
                Unit Types
              </h1>
            </div>
            <p className="text-sm lg:text-base text-gray-500 mt-1">
              Manage unit type classifications (Bachelor, 1-BR, 2-BR, etc.)
            </p>
          </div>
          <Button onClick={handleAddType} className="w-full sm:w-auto">
            <Plus size={16} className="mr-2" />
            Add Unit Type
          </Button>
        </div>

        {/* Info Box */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <p className="text-sm text-blue-800">
            <strong>Unit Types</strong> are global classifications used across all buildings.
            They help categorize units by bedroom count and layout.
          </p>
        </div>

        {/* Error State */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        {/* Unit Types List */}
        {unitTypes.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
            <Home size={48} className="mx-auto mb-4 text-gray-300" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Unit Types</h3>
            <p className="text-gray-500 mb-4">
              Create unit types to classify your rental units.
            </p>
            <Button onClick={handleAddType}>
              <Plus size={16} className="mr-2" />
              Add First Unit Type
            </Button>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-100">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-12">
                      Order
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {unitTypes.map((type, index) => (
                    <tr key={type.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2 text-gray-400">
                          <GripVertical size={16} className="cursor-move" />
                          <span className="text-sm">{index + 1}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm font-medium text-gray-900">
                          {type.name}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <button
                          onClick={() => handleEditType(type)}
                          className="text-primary hover:text-primary-hover p-1 mr-2"
                          title="Edit"
                        >
                          <Pencil size={16} />
                        </button>
                        <button
                          onClick={() => handleDeleteClick(type)}
                          className="text-red-500 hover:text-red-700 p-1"
                          title="Delete"
                        >
                          <Trash2 size={16} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="px-6 py-3 border-t border-gray-100 text-sm text-gray-500">
              {unitTypes.length} unit type{unitTypes.length !== 1 ? 's' : ''}
            </div>
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      <UnitTypeFormModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSaveType}
        unitType={editingType}
        saving={saving}
      />

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        title="Delete Unit Type"
        footer={
          <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 sm:gap-3">
            <Button variant="ghost" onClick={() => setIsDeleteModalOpen(false)} className="w-full sm:w-auto">
              Cancel
            </Button>
            <Button variant="danger" onClick={handleConfirmDelete} disabled={saving} className="w-full sm:w-auto">
              {saving ? 'Deleting...' : 'Delete Unit Type'}
            </Button>
          </div>
        }
      >
        <div className="flex items-start gap-4">
          <div className="p-2 bg-red-100 rounded-full flex-shrink-0">
            <AlertTriangle size={20} className="text-red-600" />
          </div>
          <div>
            <p className="text-gray-900">
              Are you sure you want to delete <strong>"{deletingType?.name}"</strong>?
            </p>
            <p className="text-sm text-gray-500 mt-1">
              This will fail if any units or templates use this type.
            </p>
          </div>
        </div>
      </Modal>
    </AdminLayout>
  );
}

// Form Modal Component
interface UnitTypeFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: { name: string }) => void;
  unitType: UnitType | null;
  saving: boolean;
}

function UnitTypeFormModal({ isOpen, onClose, onSave, unitType, saving }: UnitTypeFormModalProps) {
  const [name, setName] = useState(unitType?.name || '');

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setName(unitType?.name || '');
    }
  }, [isOpen, unitType]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (name.trim()) {
      onSave({ name: name.trim() });
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={unitType ? 'Edit Unit Type' : 'Add Unit Type'}
      footer={
        <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 sm:gap-3">
          <Button variant="ghost" onClick={onClose} className="w-full sm:w-auto">
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={saving || !name.trim()} className="w-full sm:w-auto">
            {saving ? 'Saving...' : unitType ? 'Save Changes' : 'Create Unit Type'}
          </Button>
        </div>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="Unit Type Name"
          placeholder="e.g., Bachelor, Jr 1-BR, 1-BR, 1BR+Den, 2-BR"
          value={name}
          onChange={(e) => setName(e.target.value)}
          hint="This name will appear in unit type dropdowns throughout the system"
          required
        />
      </form>
    </Modal>
  );
}
